import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  BackgroundJobs,
  CrmEntity, HubspotAnalysis, HubspotProperty,
  AiSequenceInput, AiSettings, AiStep,
  Account, AccountsSummary, Contact, ContactsSummary, CrmField, CrmOperator, FieldDefinitionInput, Source,
  AppNotification, Campaign, CampaignEvent, CampaignInput, CampaignStats, Company, CompanyInput,
  AnalyticsData, ApolloFilters, ApolloSearchResult, CsvPreview, BulkAction, ContactScope, EmailTemplate, EmailTemplateInput,
  EmailVerification, EnrichmentContactResult, EnrichmentEstimate, EnrichmentInput, EnrichmentRun, FieldCoverage, FieldStats, NewFieldInput,
  Segment, SegmentInput, SegmentTab, VerificationEstimate, VerificationSummary,
  DashboardData, EmailAccount, EmailAccountInput, Integration, Lead, N8nStatus, N8nWorkflow,
  Paginated, Provider, ProviderKey, User, UserInput,
} from './models';

export type QueryParams = Record<string, string | number | boolean | null | undefined>;

function toParams(query: QueryParams = {}): HttpParams {
  let params = new HttpParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== null && value !== undefined && value !== '') {
      params = params.set(key, String(value));
    }
  }
  return params;
}

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly url = environment.apiUrl;

  private list<T>(path: string, query?: QueryParams): Observable<Paginated<T>> {
    return this.http.get<Paginated<T>>(`${this.url}${path}`, { params: toParams(query) });
  }

  private one<T>(request: Observable<{ data: T }>): Observable<T> {
    return request.pipe(map((res) => res.data));
  }

  // Cuenta del usuario
  ownCompany() { return this.one(this.http.get<{ data: Company }>(`${this.url}/auth/company`)); }
  changePassword(body: { current_password?: string | null; password: string; password_confirmation: string }) {
    return this.http.put<{ message: string }>(`${this.url}/auth/password`, body);
  }

  // Empresas
  companies(query?: QueryParams) { return this.list<Company>('/companies', query); }
  createCompany(body: CompanyInput) { return this.one(this.http.post<{ data: Company }>(`${this.url}/companies`, body)); }
  aiSettings() { return this.one(this.http.get<{ data: AiSettings }>(`${this.url}/ai/settings`)); }
  saveAiSettings(body: { company_context: string | null; html_guidelines: string | null }) {
    return this.one(this.http.put<{ data: AiSettings }>(`${this.url}/ai/settings`, body));
  }
  writeSequence(body: AiSequenceInput) { return this.one(this.http.post<{ data: AiStep[] }>(`${this.url}/ai/sequence`, body)); }
  updateCompany(id: number, body: Partial<CompanyInput>) { return this.one(this.http.put<{ data: Company }>(`${this.url}/companies/${id}`, body)); }
  deleteCompany(id: number) { return this.http.delete<void>(`${this.url}/companies/${id}`); }

  // Usuarios
  users(query?: QueryParams) { return this.list<User>('/users', query); }
  createUser(body: UserInput) { return this.one(this.http.post<{ data: User }>(`${this.url}/users`, body)); }
  updateUser(id: number, body: Partial<UserInput>) { return this.one(this.http.put<{ data: User }>(`${this.url}/users/${id}`, body)); }
  deleteUser(id: number) { return this.http.delete<void>(`${this.url}/users/${id}`); }

  // Integraciones
  providers() { return this.http.get<Provider[] | { data: Provider[] }>(`${this.url}/integrations/providers`).pipe(map((r) => (Array.isArray(r) ? r : r.data))); }
  integrationMetrics() {
    return this.one(this.http.get<{ data: { providers: Partial<Record<ProviderKey, string>>; contacts_this_month: number } }>(`${this.url}/integrations/metrics`));
  }
  integrations() { return this.one(this.http.get<{ data: Integration[] }>(`${this.url}/integrations`)); }
  saveIntegration(provider: ProviderKey, body: { credentials: Record<string, unknown>; settings: Record<string, unknown> }) {
    return this.one(this.http.put<{ data: Integration }>(`${this.url}/integrations/${provider}`, body));
  }
  testIntegration(provider: ProviderKey) { return this.http.post<{ ok: boolean; message: string }>(`${this.url}/integrations/${provider}/test`, {}); }
  /** Ejecuta cada function sobre un contacto de muestra para detectar campos y créditos (consume créditos de Clay). */
  probeClayFunctions(functionIds: string[]) {
    return this.one(this.http.post<{ data: Integration }>(`${this.url}/integrations/clay/functions/probe`, { function_ids: functionIds }));
  }
  deleteIntegration(provider: ProviderKey) { return this.http.delete<void>(`${this.url}/integrations/${provider}`); }

  // Campañas
  campaigns(query?: QueryParams) { return this.list<Campaign>('/campaigns', query); }
  campaign(id: number) { return this.one(this.http.get<{ data: Campaign }>(`${this.url}/campaigns/${id}`)); }
  createCampaign(body: CampaignInput) { return this.one(this.http.post<{ data: Campaign }>(`${this.url}/campaigns`, body)); }
  updateCampaign(id: number, body: CampaignInput) { return this.one(this.http.put<{ data: Campaign }>(`${this.url}/campaigns/${id}`, body)); }
  deleteCampaign(id: number) { return this.http.delete<void>(`${this.url}/campaigns/${id}`); }
  campaignAction(id: number, action: 'launch' | 'pause' | 'resume' | 'cancel') {
    return this.one(this.http.post<{ data: Campaign }>(`${this.url}/campaigns/${id}/${action}`, {}));
  }
  campaignLeads(id: number, query?: QueryParams) { return this.list<Lead>(`/campaigns/${id}/leads`, query); }
  campaignEvents(id: number, query?: QueryParams) { return this.list<CampaignEvent>(`/campaigns/${id}/events`, query); }
  campaignStats(id: number) { return this.http.get<CampaignStats>(`${this.url}/campaigns/${id}/stats`).pipe(map(unwrap)); }

  // Cuentas de correo
  emailAccounts() { return this.one(this.http.get<{ data: EmailAccount[] }>(`${this.url}/email-accounts`)); }
  createEmailAccount(body: EmailAccountInput) { return this.http.post<unknown>(`${this.url}/email-accounts`, body); }

  // Dashboard
  dashboard(days = 30) { return this.http.get<DashboardData>(`${this.url}/dashboard`, { params: toParams({ days }) }); }

  // Analítica y plantillas
  analytics(days = 30) { return this.http.get<AnalyticsData>(`${this.url}/analytics`, { params: toParams({ days }) }); }
  templates(search?: string) { return this.one(this.http.get<{ data: EmailTemplate[] }>(`${this.url}/templates`, { params: toParams({ search }) })); }
  createTemplate(body: EmailTemplateInput) { return this.one(this.http.post<{ data: EmailTemplate }>(`${this.url}/templates`, body)); }
  updateTemplate(id: number, body: EmailTemplateInput) { return this.one(this.http.put<{ data: EmailTemplate }>(`${this.url}/templates/${id}`, body)); }
  deleteTemplate(id: number) { return this.http.delete<void>(`${this.url}/templates/${id}`); }

  // Notificaciones
  notifications(unread = false) {
    return this.http.get<{ data: AppNotification[]; unread_count: number; total: number }>(`${this.url}/notifications`, { params: toParams({ unread: unread ? 1 : null }) });
  }
  readNotification(id: string) { return this.http.post<void>(`${this.url}/notifications/${id}/read`, {}); }
  readAllNotifications() { return this.http.post<void>(`${this.url}/notifications/read-all`, {}); }
  deleteNotification(id: string) { return this.http.delete<void>(`${this.url}/notifications/${id}`); }
  deleteReadNotifications() { return this.http.delete<void>(`${this.url}/notifications/read`); }

  // Repositorio de contactos y empresas
  contacts(query?: QueryParams) { return this.list<Contact>('/contacts', query); }
  contactsSummary(query?: QueryParams) { return this.http.get<ContactsSummary>(`${this.url}/contacts/summary`, { params: toParams(query) }); }
  contact(id: number) { return this.one(this.http.get<{ data: Contact }>(`${this.url}/contacts/${id}`)); }
  updateContact(id: number, body: Partial<Contact>) { return this.one(this.http.put<{ data: Contact }>(`${this.url}/contacts/${id}`, body)); }
  accounts(query?: QueryParams) { return this.list<Account>('/accounts', query); }
  accountsSummary(query?: QueryParams) { return this.http.get<AccountsSummary>(`${this.url}/accounts/summary`, { params: toParams(query) }); }
  account(id: number) { return this.one(this.http.get<{ data: Account }>(`${this.url}/accounts/${id}`)); }
  sources(query?: QueryParams) { return this.list<Source>('/sources', query); }
  source(id: number) { return this.one(this.http.get<{ data: Source }>(`${this.url}/sources/${id}`)); }
  uploadSources(files: File[], mapping?: Record<number, string | null>) {
    const body = new FormData();
    files.forEach((f) => body.append('files[]', f, f.name));
    if (mapping) body.append('mapping', JSON.stringify(mapping));
    return this.one(this.http.post<{ data: Source[] }>(`${this.url}/sources`, body));
  }
  /** Solo hace falta el principio del fichero: cabeceras y unas filas de ejemplo. */
  previewSource(file: File) {
    const body = new FormData();
    body.append('file', file.slice(0, 256 * 1024), file.name);
    return this.http.post<CsvPreview>(`${this.url}/sources/preview`, body);
  }
  updateSource(id: number, name: string) { return this.one(this.http.put<{ data: Source }>(`${this.url}/sources/${id}`, { name })); }
  deleteSource(id: number) { return this.http.delete<void>(`${this.url}/sources/${id}`); }
  fields() { return this.http.get<{ data: CrmField[]; operators: CrmOperator[] }>(`${this.url}/fields`); }
  updateField(definitionId: number, body: FieldDefinitionInput) { return this.http.put<unknown>(`${this.url}/fields/${definitionId}`, body); }
  createField(body: NewFieldInput) { return this.http.post<unknown>(`${this.url}/fields`, body); }
  deleteField(definitionId: number) { return this.http.delete<void>(`${this.url}/fields/${definitionId}`); }
  /** Relleno de cada campo para el ámbito actual (query: search, source_id, segment_id, filters, entity). */
  fieldCoverage(query?: QueryParams) { return this.http.get<FieldCoverage>(`${this.url}/fields/coverage`, { params: toParams(query) }); }
  /** Qué hay en un campo: relleno, distintos, valores más frecuentes, rango numérico. */
  fieldStats(field: string, query?: QueryParams) { return this.http.get<FieldStats>(`${this.url}/fields/stats`, { params: toParams({ ...query, field }) }); }
  deleteContact(id: number) { return this.http.delete<void>(`${this.url}/contacts/${id}`); }
  /** Acción masiva sobre ids concretos o todo el ámbito filtrado. */
  bulkContacts(action: BulkAction, scope: ContactScope, reason?: string) {
    return this.http.post<{ affected: number }>(`${this.url}/contacts/bulk`, { action, reason, ...scope });
  }
  /** Descarga el CSV del ámbito con las columnas indicadas (devuelve el fichero como Blob). */
  exportContacts(query: QueryParams, columns: string[]) {
    let params = toParams(query);
    columns.forEach((c) => (params = params.append('columns[]', c)));
    return this.http.get(`${this.url}/contacts/export`, { params, responseType: 'blob' });
  }

  // Listas guardadas
  segments(tab: SegmentTab = 'all', withCounts = false) {
    return this.one(this.http.get<{ data: Segment[] }>(`${this.url}/segments`, { params: toParams({ tab, with_counts: withCounts ? 1 : null }) }));
  }
  segment(id: number) { return this.one(this.http.get<{ data: Segment }>(`${this.url}/segments/${id}`)); }
  /** Alta manual (origen «Añadido manualmente»); 409 si el email o el dominio ya existen. */
  createContact(body: Record<string, unknown>) {
    return this.http.post<{ data: Contact; warning: string | null }>(`${this.url}/contacts`, body);
  }
  createAccount(body: Record<string, unknown>) { return this.one(this.http.post<{ data: Account }>(`${this.url}/accounts`, body)); }
  createSegment(body: SegmentInput) { return this.one(this.http.post<{ data: Segment }>(`${this.url}/segments`, body)); }
  updateSegment(id: number, body: Partial<SegmentInput>) { return this.one(this.http.put<{ data: Segment }>(`${this.url}/segments/${id}`, body)); }
  deleteSegment(id: number) { return this.http.delete<void>(`${this.url}/segments/${id}`); }
  toggleFavoriteSegment(id: number) { return this.one(this.http.post<{ data: Segment }>(`${this.url}/segments/${id}/favorite`, {})); }

  // Enriquecimiento
  enrichments(query?: QueryParams) { return this.list<EnrichmentRun>('/enrichments', query); }
  enrichmentResults(id: number, query?: QueryParams) { return this.list<EnrichmentContactResult>(`/enrichments/${id}/results`, query); }
  enrichment(id: number) { return this.one(this.http.get<{ data: EnrichmentRun }>(`${this.url}/enrichments/${id}`)); }
  estimateEnrichment(scope: ContactScope, clay?: { fields: string[]; function_ids: string[]; only_missing: boolean }) {
    return this.http.post<EnrichmentEstimate>(`${this.url}/enrichments/estimate`, { scope, ...clay });
  }
  createEnrichment(body: EnrichmentInput) { return this.one(this.http.post<{ data: EnrichmentRun }>(`${this.url}/enrichments`, body)); }
  cancelEnrichment(id: number) { return this.one(this.http.post<{ data: EnrichmentRun }>(`${this.url}/enrichments/${id}/cancel`, {})); }

  // Verificador de emails (DeBounce o Bouncer)
  verifications(query?: QueryParams) { return this.list<EmailVerification>('/verifications', query); }
  estimateVerification(scope: ContactScope) { return this.http.post<VerificationEstimate>(`${this.url}/verifications/estimate`, { scope }); }
  verificationSummary() { return this.http.get<VerificationSummary>(`${this.url}/verifications/summary`); }
  verifyEmail(email: string) { return this.one(this.http.post<{ data: EmailVerification }>(`${this.url}/verifications`, { email })); }

  // Apollo
  apolloSearch(filters: ApolloFilters, page = 1, perPage = 25) {
    return this.http.post<ApolloSearchResult>(`${this.url}/apollo/search`, { filters, page, per_page: perPage });
  }
  apolloImport(body: { name: string; max_results: number; reveal_emails: boolean; filters: ApolloFilters; person_ids?: string[] }) {
    return this.one(this.http.post<{ data: Source }>(`${this.url}/apollo/import`, body));
  }

  backgroundJobs() { return this.http.get<BackgroundJobs>(`${this.url}/background-jobs`); }

  // HubSpot (importador)
  hubspotAnalysis() { return this.one(this.http.get<{ data: HubspotAnalysis }>(`${this.url}/hubspot/analysis`)); }
  analyzeHubspot() { return this.one(this.http.post<{ data: HubspotAnalysis }>(`${this.url}/hubspot/analysis`, {})); }
  hubspotProperties(entity: CrmEntity) {
    return this.one(this.http.get<{ data: HubspotProperty[] }>(`${this.url}/hubspot/properties`, { params: { entity } }));
  }
  /** Acepta campos y lanza la sincronización de esos campos (sync: el origen creado). */
  acceptHubspotFields(entity: CrmEntity, properties: string[]) {
    return this.http.post<{ data: { property: string; field: string; label: string }[]; sync: Source | null }>(`${this.url}/hubspot/fields`, { entity, properties });
  }
  importHubspot(body: { name?: string | null; objects: ('companies' | 'contacts')[]; mode: 'all' | 'sync'; existing: 'fill' | 'overwrite' | 'skip' }) {
    return this.one(this.http.post<{ data: Source }>(`${this.url}/hubspot/import`, body));
  }

  // n8n
  n8nStatus() { return this.http.get<N8nStatus>(`${this.url}/n8n/status`).pipe(map(unwrap)); }
  n8nWorkflows() { return this.one(this.http.get<{ data: N8nWorkflow[] }>(`${this.url}/n8n/workflows`)); }
  n8nToggle(id: string, active: boolean) {
    return this.http.post<unknown>(`${this.url}/n8n/workflows/${id}/${active ? 'activate' : 'deactivate'}`, {});
  }
}

/** Acepta tanto `{...}` como `{ data: {...} }` (recursos Laravel). */
function unwrap<T>(res: T | { data: T }): T {
  return res && typeof res === 'object' && 'data' in (res as object) && !Array.isArray(res)
    ? (res as { data: T }).data
    : (res as T);
}
